import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  calendarDateToLocalDate,
  formatCalendarDate,
  getVisaComplementaryTypeLabel,
  toCalendarDateString,
} from '../../../../../../helper/visaComplementaryDocuments';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import { isSessionExpiredError } from '../../../../../../utils/authError';
import Banner from '../../../../../banner/banner';
import Divider from '../../../../../divider/divider';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';
import {
  DateInput,
  DocumentSidebar,
  DocumentViewer,
  ModalHeader,
  UserInfoGrid,
  ValidationActions,
  openNextUnverifiedDocument,
  useAdminModalFiles,
  useAdminModalTempWorker,
  useAllUserDocuments,
} from '../shared';

const ADMIN_TYPE = 'visa_complementary';

const ERROR_KEYS = {
  VISA_COMPLEMENTARY_BAD_STATE: 'badState',
  INVALID_END_VALIDITY: 'invalidEndValidity',
  VISA_COMPLEMENTARY_NOT_FOUND: 'notFound',
};

const canAcceptProof = (proof) => {
  const status = String(proof?.validated ?? '');
  return status === '1' || status === '3';
};

const canRefuseProof = (proof) => {
  const status = String(proof?.validated ?? '');
  return status === '1' || status === '2';
};

const AdminModalFileVisaComplementary = () => {
  const { toggle, switchModal } = UseModal();
  const {
    adminTempWorkerId,
    adminUserId,
    adminComplementaryDocumentId,
    setUpdateTempWorker,
    setAdminType,
    language,
  } = UseContext();
  const token = localStorage.getItem('xsrfToken');

  const [endValidity, setEndValidity] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const text = (key, fallback = '') =>
    language.getValue(`visaComplementary.${key}`, fallback);

  const { docs, isWaiting } = useAdminModalFiles({
    token,
    adminUserId,
    adminType: ADMIN_TYPE,
    adminFileName: adminComplementaryDocumentId,
    enabled: !!adminUserId && !!adminComplementaryDocumentId,
  });

  const { tempWorker, user, isLoading } = useAdminModalTempWorker({
    token,
    adminTempWorkerId,
    enabled: !!adminTempWorkerId,
  });

  const { allDocuments, isLoading: isLoadingAllDocs } = useAllUserDocuments({
    token,
    adminUserId,
    currentType: ADMIN_TYPE,
    enabled: !!adminUserId,
  });

  const proof = useMemo(
    () =>
      (tempWorker?.visa?.complementaryDocuments ?? []).find(
        (document) =>
          String(document?._id) === String(adminComplementaryDocumentId),
      ) ?? null,
    [tempWorker, adminComplementaryDocumentId],
  );

  // A pending proof has no end_validity until it is validated: the date the AI
  // read is only a suggestion the admin checks on the document.
  useEffect(() => {
    setEndValidity(
      calendarDateToLocalDate(proof?.end_validity ?? proof?.end_validity_ai),
    );
  }, [proof]);

  const notRead = text('modal.notRead', 'Non lu');
  const empty = text('modal.empty', '—');

  const showError = (err) => {
    setIsSubmitting(false);
    if (err?.name === 'CanceledError') return;
    // The api layer already signs the admin out on a real session failure.
    if (isSessionExpiredError(err)) return;
    const key = ERROR_KEYS[err?.response?.data?.errorCode] || 'generic';
    toast.error(text(`errors.${key}`));
  };

  const modalAcceptSubmit = () => {
    const endValidityDay = toCalendarDateString(endValidity);
    if (!endValidityDay) {
      toast.error(text('toasts.endValidityRequired'));
      return;
    }
    setIsSubmitting(true);
    ApiDatabase.postAcceptTempWorkerVisaComplementaryDocument(
      {
        adminTempWorkerId,
        documentId: adminComplementaryDocumentId,
        end_validity: endValidityDay,
      },
      (data) => {
        setIsSubmitting(false);
        setUpdateTempWorker({
          tempWorkerId: data?._id,
          documentType: ADMIN_TYPE,
          action: 'accept',
          tempWorker: data,
        });
        toast.success(text('toasts.accepted'));
        openNextUnverifiedDocument({
          tempWorker,
          allDocuments,
          currentType: ADMIN_TYPE,
          setAdminType,
          switchModal,
          toggle,
        });
      },
      showError,
    );
  };

  // The proof openers only set the document id: the refuse modal reads
  // adminType to pick the proof route and to come back here.
  const handleRefuse = () => {
    setAdminType(ADMIN_TYPE);
    switchModal('ad_file_refuse');
  };

  const handleOpenDocumentModal = (type, modalName) => {
    setAdminType(type);
    switchModal(modalName);
  };

  const showAccept = canAcceptProof(proof);
  const showRefuse = canRefuseProof(proof);

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        <DocumentSidebar
          allDocuments={allDocuments}
          isLoading={isLoadingAllDocs}
          currentType={ADMIN_TYPE}
          onOpenDocumentModal={handleOpenDocumentModal}
          tempWorker={tempWorker}
        />

        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          <DocumentViewer docs={docs} isWaiting={isWaiting} />
        </div>

        <div className="w-80 flex flex-col gap-3 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <ModalHeader
            adminType={ADMIN_TYPE}
            tempWorker={tempWorker}
            docsCount={docs.length}
            onClose={toggle}
            label={text('modal.title')}
          />

          {!isLoading && !proof && (
            <Banner type="alert">{text('modal.notFound')}</Banner>
          )}

          <UserInfoGrid
            user={user}
            validationStatus={
              proof?.validated === undefined || proof?.validated === null
                ? undefined
                : String(proof.validated)
            }
            reason={proof?.ai?.reason}
          />

          {proof && (
            <>
              <Divider />

              <div className="grid grid-cols-2 gap-4 ft-sm">
                <div>{text('modal.documentType')}</div>
                <div className="text-gray-500">
                  {getVisaComplementaryTypeLabel(proof.type, language) || empty}
                </div>

                <div>{text('modal.currentPermitEnd')}</div>
                <div className="text-gray-500">
                  {formatCalendarDate(tempWorker?.visa?.end_validity) || empty}
                </div>

                <div>{text('modal.aiEndValidity')}</div>
                <div className="text-gray-500">
                  {formatCalendarDate(proof.end_validity_ai) || notRead}
                </div>
              </div>

              <DateInput
                label={text('modal.endValidityLabel')}
                value={endValidity}
                onChange={setEndValidity}
              />
            </>
          )}

          <ValidationActions
            onRefuse={handleRefuse}
            onAccept={modalAcceptSubmit}
            showAccept={showAccept}
            showRefuse={showRefuse}
            isAcceptDisabled={isSubmitting}
          />
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileVisaComplementary;
